import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { forwardRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * `PasswordField` — MUI TextField with a show/hide toggle.
 *
 * Used at every password-CHOOSING / password-ENTERING surface in the
 * admin SPA:
 *
 *   - `LoginStep` — password input (no policy preview)
 *   - `SetPasswordPage` — invite-accept new password + confirm
 *   - `ResetConfirmPage` — reset new password + confirm
 *
 * The toggle is rendered via the MUI `endAdornment` slot — keeps the
 * field height + label-shrink behaviour consistent with the other
 * TextFields in the auth pages. The IconButton has `aria-label` so
 * screen-readers announce the show/hide action; the icon itself is
 * aria-hidden via MUI's default for SvgIcons inside `IconButton`.
 *
 * Aria-labels are resolved via i18n (`auth.common.password.showAriaLabel`
 * / `hideAriaLabel`) — keeps the component compliant with CLAUDE.md
 * absolute rule #11 (no hardcoded user-visible strings, including a11y
 * labels) without forcing every caller to thread `t(...)` props through.
 *
 * Inherits every other TextField prop (label, placeholder, error,
 * helperText, fullWidth, disabled, autoComplete, etc.). `forwardRef` so
 * React Hook Form's `register` / `Controller` can wire the ref into the
 * underlying input.
 */
export type PasswordFieldProps = Omit<TextFieldProps, 'type'>;

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ InputProps, ...rest }, ref): ReactElement {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const toggle = (): void => setVisible((v) => !v);
    return (
      <TextField
        {...rest}
        type={visible ? 'text' : 'password'}
        inputRef={ref}
        InputProps={{
          ...InputProps,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={
                  visible
                    ? t('auth.common.password.hideAriaLabel')
                    : t('auth.common.password.showAriaLabel')
                }
                onClick={toggle}
                edge="end"
                tabIndex={-1}
                size="small"
              >
                {visible ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  },
);
